import { DialogComponent } from '@theme/dialog';

class ProductLabelsDialog extends DialogComponent {
  requiredRefs = ['dialog', 'title', 'description'];

  /** @type {AbortController | null} */
  #documentClickAbort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#documentClickAbort = new AbortController();
    document.addEventListener('click', this.#handleDocumentClick, {
      capture: true,
      signal: this.#documentClickAbort.signal,
    });
    this.refs.dialog.addEventListener('click', this.#stopModalClickBubbling);
  }

  disconnectedCallback() {
    this.#documentClickAbort?.abort();
    this.#documentClickAbort = null;
    this.refs.dialog?.removeEventListener('click', this.#stopModalClickBubbling);
    super.disconnectedCallback();
  }

  #stopModalClickBubbling = (/** @type {MouseEvent} */ event) => {
    event.stopPropagation();
  };

  /**
   * @param {string} value
   * @returns {string}
   */
  static #escape(value) {
    return String(value).replace(
      /[&<>"']/g,
      (teken) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[teken] ?? teken
    );
  }

  /**
   * Tekst binnen een alinea of opsommingsregel: vet, cursief en verwijzingen.
   * @param {any[]} children
   * @returns {string}
   */
  static #inlineToHtml(children = []) {
    let html = '';
    for (const node of children) {
      if (node?.type === 'text' && typeof node.value === 'string') {
        let text = ProductLabelsDialog.#escape(node.value).replace(/\n/g, '<br>');
        if (node.bold) text = `<strong>${text}</strong>`;
        if (node.italic) text = `<em>${text}</em>`;
        html += text;
      } else if (node?.type === 'link' && node.url) {
        // Een verwijzing naar de eigen winkel blijft in hetzelfde venster; alleen wie
        // de winkel verlaat krijgt een nieuw tabblad.
        const extern = /^https?:\/\//i.test(node.url) && !node.url.includes(window.location.host);
        const doel = extern ? ' target="_blank" rel="noopener noreferrer"' : '';
        html += `<a href="${ProductLabelsDialog.#escape(node.url)}"${doel}>${ProductLabelsDialog.#inlineToHtml(
          node.children ?? []
        )}</a>`;
      } else if (Array.isArray(node?.children)) {
        html += ProductLabelsDialog.#inlineToHtml(node.children);
      }
    }
    return html;
  }

  /**
   * Blokken uit het rich-text-veld. Opsommingen horen er expliciet bij: de uitleg van
   * de lijmactie stelt de vraag "Hoe werkt de actie?" en beantwoordt hem met een
   * lijstje, dat anders spoorloos wegviel.
   * @param {any[]} children
   * @returns {string}
   */
  static #blocksToHtml(children = []) {
    let html = '';
    for (const node of children) {
      switch (node?.type) {
        case 'paragraph':
          html += `<p>${ProductLabelsDialog.#inlineToHtml(node.children ?? [])}</p>`;
          break;
        case 'heading': {
          const niveau = Math.min(Math.max(Number(node.level) || 3, 2), 6);
          html += `<h${niveau}>${ProductLabelsDialog.#inlineToHtml(node.children ?? [])}</h${niveau}>`;
          break;
        }
        case 'list': {
          const tag = node.listType === 'ordered' ? 'ol' : 'ul';
          const regels = (node.children ?? [])
            .map((item) => `<li>${ProductLabelsDialog.#inlineToHtml(item?.children ?? [])}</li>`)
            .join('');
          html += `<${tag}>${regels}</${tag}>`;
          break;
        }
        default:
          if (Array.isArray(node?.children)) html += ProductLabelsDialog.#blocksToHtml(node.children);
      }
    }
    return html;
  }

  /**
   * @param {string} raw
   * @returns {string}
   */
  static #richTextToHtml(raw) {
    if (!raw) return '';
    try {
      const data = JSON.parse(raw);
      if (data?.type === 'root' && Array.isArray(data.children)) {
        return ProductLabelsDialog.#blocksToHtml(data.children);
      }
    } catch {
      // Geen rich text: de aanroeper valt terug op de platte tekst.
    }
    return '';
  }

  #handleDocumentClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('.product-labels__label--clickable');
    if (!trigger) return;

    const description = trigger.getAttribute('data-description') ?? '';
    if (!description) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const labelText = trigger.querySelector('.product-labels__label-text')?.textContent ?? '';
    this.refs.title.textContent = labelText;

    const html = ProductLabelsDialog.#richTextToHtml(description);
    if (html) {
      this.refs.description.innerHTML = html;
    } else {
      this.refs.description.textContent = description;
    }

    // De uitleg vertelt wat de actie inhoudt; de knop brengt je naar de pagina die
    // hem helemaal uitlegt. Geen verwijzing op het label = geen knop.
    const cta = this.refs.cta;
    if (cta instanceof HTMLAnchorElement) {
      const url = trigger.getAttribute('data-link-url');
      if (url) {
        cta.href = url;
        cta.textContent = trigger.getAttribute('data-link-label') || 'Bekijk de actie';
        cta.hidden = false;
      } else {
        cta.hidden = true;
        cta.removeAttribute('href');
      }
    }

    this.showDialog();
  };
}

if (!customElements.get('product-labels-dialog')) {
  if (!customElements.get('product-labels-dialog')) customElements.define('product-labels-dialog', ProductLabelsDialog);
}
